import { Component, lazy, Suspense, useEffect, useRef, useState, type ReactNode } from "react"
import { cn } from "@/lib/utils"
import type { DitherProps } from "@/components/homepage/animations/dither"

function importWithRetry() {
  return import("@/components/homepage/animations/dither").then((m) => ({ default: m.Dither })).catch((err) => {
    // Stale chunk after deploy — reload once to get fresh assets
    const key = "dither-chunk-reload"
    if (!sessionStorage.getItem(key)) {
      sessionStorage.setItem(key, "1")
      window.location.reload()
    }
    throw err
  })
}

const DynamicDither = lazy(importWithRetry)

class DitherErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false }
  static getDerivedStateFromError() { return { hasError: true } }
  render() { return this.state.hasError ? null : this.props.children }
}

export type LazyDitherProps = DitherProps & {
  className?: string
  placeholderClassName?: string
  rootMargin?: string
}

export function LazyDither({
  className,
  placeholderClassName,
  rootMargin = "200px",
  ...props
}: LazyDitherProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [shouldMount, setShouldMount] = useState(false)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    if (shouldMount) return

    const io = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return
        setShouldMount(true)
        io.disconnect()
      },
      { root: null, rootMargin, threshold: 0.01 }
    )

    io.observe(el)
    return () => io.disconnect()
  }, [rootMargin, shouldMount])

  useEffect(() => {
    if (!shouldMount) return
    const raf = requestAnimationFrame(() => setVisible(true))
    return () => cancelAnimationFrame(raf)
  }, [shouldMount])

  return (
    <div ref={containerRef} className={cn("relative h-full w-full", className)}>
      <div
        aria-hidden="true"
        className={cn(
          "absolute inset-0 bg-linear-to-r from-muted/10 via-muted/25 to-muted/10",
          placeholderClassName
        )}
      />

      {shouldMount ? (
        <div
          className={cn(
            "absolute inset-0 transition-opacity duration-500",
            visible ? "opacity-100" : "opacity-0"
          )}
        >
          <DitherErrorBoundary>
            <Suspense fallback={null}>
              <DynamicDither {...props} />
            </Suspense>
          </DitherErrorBoundary>
        </div>
      ) : null}
    </div>
  )
}

/** Grid crossbar section divider — thin line with + markers at edges and center. */
export function DitherDivider() {
  return (
    <div className="relative h-6 flex items-center border-b border-border">
      <div className="absolute inset-x-0 top-1/2 h-px bg-border" />
      {/* Left + */}
      <Plus className="absolute left-0" />
      {/* Center + */}
      <Plus className="absolute left-1/2 -translate-x-1/2" />
      {/* Right + */}
      <Plus className="absolute right-0" />
    </div>
  )
}

function Plus({ className }: { className?: string }) {
  return (
    <div className={cn("relative w-3 h-3", className)}>
      <div className="absolute left-1/2 top-0 bottom-0 w-px -translate-x-1/2 bg-muted-foreground/50" />
      <div className="absolute top-1/2 left-0 right-0 h-px -translate-y-1/2 bg-muted-foreground/50" />
    </div>
  )
}
